import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Loader } from "@/components/ui/Loader";
import { formatDateBr } from "@/lib/gente-gestao/format";
import { downloadGgFile } from "@/services/genteGestao";
import { extractEscutaError, fetchProtocolo, pdfProtocoloUrl, type EscutaPublica } from "@/services/escuta";

export function EscutaProtocoloPage() {
  const { protocolo = "" } = useParams<{ protocolo: string }>();
  const [item, setItem] = useState<EscutaPublica | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [baixando, setBaixando] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    fetchProtocolo(protocolo)
      .then((res) => {
        if (!cancel) setItem(res);
      })
      .catch((e) => {
        if (!cancel) setErro(extractEscutaError(e, "Protocolo não encontrado."));
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [protocolo]);

  async function baixarPdf() {
    if (!item) return;
    setBaixando(true);
    try {
      await downloadGgFile(pdfProtocoloUrl(item.protocolo), `escuta-${item.protocolo}.pdf`, "application/pdf");
    } catch (e) {
      setErro(extractEscutaError(e, "Não foi possível baixar o PDF."));
    } finally {
      setBaixando(false);
    }
  }

  return (
    <div className="flex h-dvh min-h-0 flex-col overflow-y-auto overflow-x-hidden overscroll-y-contain bg-slate-100 text-slate-800">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-xl items-center justify-center px-4 py-3">
          <span className="text-sm font-bold tracking-wide text-brand-700">SEMOG · Canal de Escuta Ativa</span>
        </div>
      </header>
      <main className="mx-auto w-full max-w-xl px-4 py-6 pb-28">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader label="Consultando protocolo..." />
          </div>
        ) : !item ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-card">
            <h1 className="text-xl font-bold">Protocolo não encontrado</h1>
            <p className="mt-3 text-sm text-slate-600">{erro}</p>
            <Link to="/escuta" className="mt-4 inline-block text-sm font-semibold text-brand-700">
              Registrar uma manifestação
            </Link>
          </div>
        ) : (
          <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{item.protocolo}</p>
            <h1 className="text-xl font-bold">{item.assunto}</h1>
            <p className="text-sm text-slate-500">
              {item.tipoLabel} · {item.statusLabel} · {formatDateBr(item.createdAt)}
            </p>
            {erro && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{erro}</p>}
            <p className="text-sm font-medium text-slate-700">
              {item.anonima ? "Identificação: anônima" : `Identificação: ${item.nome}`}
            </p>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{item.relato}</p>
            <div className="rounded-xl bg-slate-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Retorno</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">
                {item.resposta || "Ainda não há retorno registrado. A análise segue com sigilo."}
              </p>
            </div>
            <Button loading={baixando} onClick={() => void baixarPdf()}>
              Baixar PDF com QR Code
            </Button>
            <Link to="/escuta" className="text-center text-sm font-semibold text-brand-700">
              Nova manifestação
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
